function getShopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function normaliseSearchText(value) {
    return String(value || '')
      .trim()
      .toLocaleLowerCase('pt-PT');
  }

  function getCollectionData(collection) {
    return collection?.collection || collection || {};
  }

  function getCollectionHandle(collection) {
    const collectionData = getCollectionData(collection);

    if (collectionData.handle) {
      return String(collectionData.handle).trim().toLowerCase();
    }

    const url = collectionData.url || '';
    const match = url.match(/\/collections\/([^/?#]+)/);

    return match ? decodeURIComponent(match[1]).trim().toLowerCase() : '';
  }

  function getCollectionUrl(collection) {
    const collectionData = getCollectionData(collection);

    if (collectionData.url) return collectionData.url;

    const handle = getCollectionHandle(collectionData);
    return handle ? `${getShopRoot()}collections/${handle}` : '#';
  }

  function getCollectionTitle(collection) {
    return getCollectionData(collection)?.title || '';
  }

  function getCollectionTemplateSuffix(collection) {
    const collectionData = getCollectionData(collection);

    return normaliseSearchText(collectionData.template_suffix || collectionData.templateSuffix || '');
  }

  function getBrandMenuHandles() {
    const element = document.querySelector('#bioself-brand-menu-handles');

    if (!element) return new Set();

    try {
      const jsonText = (element.textContent || '[]').trim().replace(/,\s*]$/, ']');
      const handles = JSON.parse(jsonText || '[]');

      return new Set(handles.map((handle) => normaliseSearchText(handle)).filter(Boolean));
    } catch (error) {
      console.error('Não foi possível ler o menu marcas.', error);
      return new Set();
    }
  }

  const brandMenuHandles = getBrandMenuHandles();

  function isBrandCollection(collection) {
    const handle = getCollectionHandle(collection);
    const templateSuffix = getCollectionTemplateSuffix(collection);

    return (handle && brandMenuHandles.has(handle)) || templateSuffix === 'brand';
  }

  async function searchCollectionsFromApp(query, signal) {
    const params = new URLSearchParams({
      action: 'search_collections',
      q: query,
      limit: '8',
    });

    const response = await fetch(`https://bioself.apps.bydas.com/?${params.toString()}`, { signal });

    if (!response.ok) {
      throw new Error(`Erro ${response.status} ao pesquisar coleções na aplicação.`);
    }

    const responseData = await response.json();
    const errors = responseData?.errors;

    if (errors) {
      throw new Error(errors.map((error) => error.message).join(', '));
    }

    let collections = responseData?.data?.collections || responseData?.collections || null;

    if (!collections) {
      const singleCollection = responseData?.data?.collection || responseData?.collection || null;

      collections = singleCollection ? [singleCollection] : [];
    }

    return collections.map(getCollectionData);
  }

  async function searchCollectionsFromShopify(query, signal) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'collection',
      'resources[limit]': '8',
    });

    const response = await fetch(`${getShopRoot()}search/suggest.json?${params.toString()}`, { signal });

    if (!response.ok) {
      throw new Error(`Erro ${response.status} ao pesquisar coleções na Shopify.`);
    }

    const data = await response.json();

    return (data?.resources?.results?.collections || data?.resources?.collections || []).map(getCollectionData);
  }

  function mergeCollections(shopifyCollections, appCollections) {
    const collectionsByKey = new Map();

    const addCollection = (collection, overwrite = false) => {
      const collectionData = getCollectionData(collection);
      const handle = getCollectionHandle(collectionData);
      const title = normaliseSearchText(getCollectionTitle(collectionData));
      const key = handle ? `handle:${handle}` : title ? `title:${title}` : '';

      if (!key) return;

      if (overwrite || !collectionsByKey.has(key)) {
        collectionsByKey.set(key, collectionData);
      }
    };

    /*
     * Primeiro entram os resultados da Shopify. Depois, os dados da aplicação
     * substituem os equivalentes, porque podem incluir template_suffix.
     */
    shopifyCollections.forEach((collection) => addCollection(collection));
    appCollections.forEach((collection) => addCollection(collection, true));

    return Array.from(collectionsByKey.values());
  }

  /*
   * Guarda o resultado da validação por handle para que a mesma coleção
   * não seja consultada novamente a cada carácter introduzido na pesquisa.
   */
  const activeCollectionProductsCache = new Map();

  function getCollectionProductsCount(collection) {
    const collectionData = getCollectionData(collection);
    const rawCount = collectionData.products_count ?? collectionData.productsCount ?? null;

    if (rawCount === null || rawCount === undefined || rawCount === '') {
      return null;
    }

    const count = Number(rawCount);
    return Number.isFinite(count) ? count : null;
  }

  async function collectionHasActiveProduct(collection) {
    const handle = getCollectionHandle(collection);

    if (!handle) return false;

    if (activeCollectionProductsCache.has(handle)) {
      return activeCollectionProductsCache.get(handle);
    }

    const knownProductsCount = getCollectionProductsCount(collection);

    /*
     * Uma contagem igual a zero permite excluir imediatamente a coleção.
     * Para valores superiores a zero, ainda validamos no endpoint público,
     * garantindo que existe pelo menos um produto ativo e publicado.
     */
    if (knownProductsCount === 0) {
      activeCollectionProductsCache.set(handle, false);
      return false;
    }

    try {
      const params = new URLSearchParams({ limit: '1' });
      const response = await fetch(
        `${getShopRoot()}collections/${encodeURIComponent(handle)}/products.json?${params.toString()}`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Erro ${response.status} ao validar os produtos da coleção ${handle}.`);
      }

      const data = await response.json();
      const hasActiveProduct = Array.isArray(data?.products) && data.products.length > 0;

      activeCollectionProductsCache.set(handle, hasActiveProduct);
      return hasActiveProduct;
    } catch (error) {
      console.warn(`Não foi possível validar os produtos ativos da coleção ${handle}.`, error);

      /*
       * Fallback: se o endpoint público falhar, utiliza a contagem devolvida
       * pela aplicação, quando disponível.
       */
      const fallbackResult = knownProductsCount !== null && knownProductsCount > 0;

      activeCollectionProductsCache.set(handle, fallbackResult);
      return fallbackResult;
    }
  }

  async function filterCollectionsWithActiveProducts(collections) {
    const validatedCollections = await Promise.all(
      collections.map(async (collection) => {
        const hasActiveProduct = await collectionHasActiveProduct(collection);

        return hasActiveProduct ? collection : null;
      })
    );

    return validatedCollections.filter(Boolean);
  }

  async function searchProductsHtml(query, signal) {
    const params = new URLSearchParams({
      view: 'ajax',
      q: query,
      'options[prefix]': 'last',
      type: 'product',
    });

    const response = await fetch(`${getShopRoot()}search?${params.toString()}`, { signal });

    if (!response.ok) {
      throw new Error(`Erro ${response.status} ao pesquisar produtos.`);
    }

    return response.text();
  }

  function renderSearchGroup(container, items, label, groupClass) {
    if (!items.length) return;

    const group = document.createElement('div');
    group.classList.add('search-bar__result-group', `search-bar__result-${groupClass}`);

    items.forEach((item) => {
      const url = getCollectionUrl(item);
      if (url === '#') return;

      const link = document.createElement('a');
      link.href = url;
      link.classList.add('search-bar__result-item');

      const info = document.createElement('div');
      info.classList.add('search-bar__item-info', 'search-bar__item-info--entity');

      const title = document.createElement('p');
      title.classList.add('search-bar__item-title');
      title.innerText = getCollectionTitle(item);

      const type = document.createElement('span');
      type.classList.add('search-bar__item-type');
      type.innerText = label;

      info.appendChild(title);
      info.appendChild(type);
      link.appendChild(info);
      group.appendChild(link);
    });

    if (group.children.length) {
      container.appendChild(group);
    }
  }

  function appendProductsHtml(container, html) {
    if (!html) return;

    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(html, 'text/html');

    Array.from(parsedHTML.body.children).forEach((element) => {
      container.appendChild(element);
    });
  }

  async function loadSearchResults(container, query, signal) {
    const results = await Promise.allSettled([
      searchCollectionsFromApp(query, signal),
      searchCollectionsFromShopify(query, signal),
      searchProductsHtml(query, signal),
    ]);

    if (signal.aborted) {
      throw new DOMException('Pesquisa cancelada.', 'AbortError');
    }

    const [appCollectionsResult, shopifyCollectionsResult, productsResult] = results;

    const appCollections = appCollectionsResult.status === 'fulfilled' ? appCollectionsResult.value : [];

    const shopifyCollections = shopifyCollectionsResult.status === 'fulfilled' ? shopifyCollectionsResult.value : [];

    const productsHtml = productsResult.status === 'fulfilled' ? productsResult.value : '';

    results.forEach((result) => {
      if (result.status === 'rejected' && result.reason?.name !== 'AbortError') {
        console.error('Erro na pesquisa:', result.reason);
      }
    });

    const collections = mergeCollections(shopifyCollections, appCollections);

    const activeCollections = await filterCollectionsWithActiveProducts(collections);

    if (signal.aborted) {
      throw new DOMException('Pesquisa cancelada.', 'AbortError');
    }

    const brands = [];
    const categories = [];
    const brandTitles = new Set();
    const categoryTitles = new Set();

    /*
     * Primeiro adicionamos as marcas, garantindo títulos únicos.
     * Depois adicionamos as categorias, ignorando:
     * - categorias repetidas;
     * - categorias cujo título já exista numa marca.
     *
     * O título é normalizado para que diferenças de maiúsculas,
     * minúsculas, espaços ou acentos não criem duplicados.
     */
    activeCollections.forEach((collection) => {
      if (!isBrandCollection(collection)) return;

      const titleKey = normaliseSearchText(getCollectionTitle(collection));

      if (!titleKey || brandTitles.has(titleKey)) return;

      brandTitles.add(titleKey);
      brands.push(collection);
    });

    activeCollections.forEach((collection) => {
      if (isBrandCollection(collection)) return;

      const titleKey = normaliseSearchText(getCollectionTitle(collection));

      if (!titleKey || brandTitles.has(titleKey) || categoryTitles.has(titleKey)) {
        return;
      }

      categoryTitles.add(titleKey);
      categories.push(collection);
    });

    renderSearchGroup(container, brands, 'Marca', 'brands');
    renderSearchGroup(container, categories, 'Categoria', 'categories');
    appendProductsHtml(container, productsHtml);

    const firstCollection = brands[0] || categories[0] || null;
    return firstCollection ? getCollectionUrl(firstCollection) : null;
  }

  function setupSearchForm(form) {
    if (!form) return;

    const resultsContainer = form.querySelector('#search_results_modal');
    const input = form.querySelector('input[type="search"]');

    if (!resultsContainer || !input) return;

    let currentController = null;
    let firstCollectionLink = null;

    input.addEventListener('input', async () => {
      const query = input.value.trim();

      if (currentController) {
        currentController.abort();
        currentController = null;
      }

      if (!query) {
        resultsContainer.replaceChildren();
        resultsContainer.style.display = 'none';
        form.style.boxShadow = '0 4px 0 rgba(255, 165, 0, 0.6)';
        firstCollectionLink = null;
        return;
      }

      resultsContainer.replaceChildren();
      resultsContainer.style.display = 'grid';
      form.style.boxShadow = 'unset';

      currentController = new AbortController();
      const { signal } = currentController;

      try {
        firstCollectionLink = await loadSearchResults(resultsContainer, query, signal);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Erro ao carregar os resultados da pesquisa:', error);
        }
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const query = input.value.trim();
      if (!query) return;

      const makeUrl = (type) => {
        const params = new URLSearchParams({
          q: query,
          'options[prefix]': 'last',
          type,
        });

        return `${getShopRoot()}search?${params.toString()}`;
      };

      try {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '1',
        });

        const response = await fetch(`${getShopRoot()}search/suggest.json?${params.toString()}`);

        if (!response.ok) {
          throw new Error(`Erro ${response.status} ao validar produtos.`);
        }

        const data = await response.json();
        const productsA = data?.resources?.results?.products || [];
        const productsB = data?.resources?.products || [];
        const hasProduct = productsA.length > 0 || productsB.length > 0;

        if (hasProduct) {
          window.location.href = makeUrl('product');
        } else if (firstCollectionLink) {
          window.location.href = firstCollectionLink;
        } else {
          window.location.href = makeUrl('article');
        }
      } catch (error) {
        console.warn('Falha na validação final da pesquisa; a abrir os produtos.', error);
        window.location.href = makeUrl('product');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector('#search_form');
    const formMobile = document.querySelector('#search_form_mobile');
    const liMobile = document.querySelector('#open-search-bar-mobile');

    if (liMobile && formMobile) {
      liMobile.addEventListener('click', () => {
        const isOpen = formMobile.style.display === 'grid';
        formMobile.style.display = isOpen ? 'none' : 'grid';

        if (!isOpen) {
          formMobile.querySelector('input[type="search"]')?.focus();
        }
      });
    }

    setupSearchForm(form);
    setupSearchForm(formMobile);
  });
